import axios, { AxiosError, AxiosInstance } from "axios";
import { getApiHostname } from "../config/getApiHostname";

const hostname = getApiHostname();

axios.defaults.baseURL = hostname;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common.accept = "application/json";

const instance: AxiosInstance = axios.create({
  baseURL: hostname,
});

// Module-level re-auth handler set by React code via setOnUnauthorized().
// Invoked on any 401 response — the handler should trigger re-login (e.g.
// show a modal or redirect to the IdP). We coalesce concurrent 401s so only
// a single re-auth is kicked off even when multiple inflight polls fail.
let onUnauthorized: (() => Promise<void>) | null = null;
let inflightReauth: Promise<void> | null = null;

export const setOnUnauthorized = (
  handler: (() => Promise<void>) | null
): void => {
  onUnauthorized = handler;
};

instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && onUnauthorized) {
      if (!inflightReauth) {
        inflightReauth = (async () => {
          try {
            await onUnauthorized!();
          } finally {
            inflightReauth = null;
          }
        })();
      }
      // We don't await the relogin here: the individual request should still
      // reject so React Query's onError / retry logic fires. The next poll
      // interval or retry will pick up the fresh token from getAuthTokenBlocking.
    }
    return Promise.reject(error);
  }
);

export default instance;
